import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Table2, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  ReferenceDataTabs,
  ReferenceDataTabsContent,
  ReferenceDataTabsList,
  ReferenceDataTabsTrigger,
} from "@/components/admin/reference-data/ReferenceDataTabs";
import { CompactHero } from "@/components/brand/CompactHero";
import { HeroMetaItem, HeroMetaSep } from "@/components/brand/Hero";
import { useReferenceDataSources } from "@/hooks/useApi";
import type { ReferenceDataSource } from "@/types/reference-data";
import { formatRelative } from "@/lib/datetime";
import { sourceLabels } from "@/lib/reference-data-labels";
import { RecordsTab } from "@/components/admin/reference-data/RecordsTab";
import { UpdateTab } from "@/components/admin/reference-data/UpdateTab";
import { VersionsTab } from "@/components/admin/reference-data/VersionsTab";

export default function AdminReferenceDataSource() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tableName = "" } = useParams<{ tableName: string }>();

  const { data: sourcesData } = useReferenceDataSources();
  const source = useMemo<ReferenceDataSource | undefined>(
    () => sourcesData?.sources.find((s) => s.table_name === tableName),
    [sourcesData?.sources, tableName],
  );

  // The in-progress upload/ingest lives at the page level, not inside the tab.
  // Radix unmounts the inactive tab, so keeping it here means the staged draft
  // (and its Promote button) survives switching to Records/Versions and back.
  const [draftFile, setDraftFile] = useState<File | null>(null);
  const [draftRunId, setDraftRunId] = useState<string | undefined>(undefined);
  // Controlled so staging a row-level edit can jump the user to Versions & audit
  // (where the fresh draft is promoted — the same Draft → Promote flow).
  const [tab, setTab] = useState("records");

  const labels = sourceLabels(t, source);

  const kpis = [
    { label: t("Active version"), value: source?.active_version ?? "—", gold: true },
    { label: t("Active rows"), value: (source?.active_row_count ?? 0).toLocaleString() },
    {
      label: t("Promoted"),
      value: source?.promoted_at ? formatRelative(source.promoted_at) : "—",
      sub: source?.promoted_by ? `${t("by")} ${source.promoted_by}` : undefined,
    },
    { label: t("Category"), value: source?.category === "sap_package" ? t("SAP") : t("Curation") },
  ];

  return (
    <div className="-m-6">
      <CompactHero
        glyph={source?.category === "sap_package" ? <Database /> : <Table2 />}
        overline={
          source?.category === "sap_package"
            ? t("SAP SOURCE · UPLOAD & INGEST")
            : t("CURATION · AUTHORED IN-HOUSE")
        }
        title={tableName}
        meta={
          <>
            <HeroMetaItem k={t("Reference source")} v={labels.displayName || "…"} />
            <HeroMetaSep />
            <HeroMetaItem k={t("Origin")} v={labels.origin || "…"} />
            {source?.active_version && (
              <>
                <HeroMetaSep />
                <HeroMetaItem k={t("Active")} v={source.active_version} />
              </>
            )}
          </>
        }
        actions={
          <Button
            variant="ghost-light"
            size="sm"
            className="h-9 gap-2 px-3.5 text-xs"
            onClick={() => navigate("/admin/reference-data")}
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {t("Back to inventory")}
          </Button>
        }
        kpis={kpis}
      />

      <div className="p-8">
        <ReferenceDataTabs value={tab} onValueChange={setTab}>
          <ReferenceDataTabsList>
            <ReferenceDataTabsTrigger value="records">{t("Records")}</ReferenceDataTabsTrigger>
            <ReferenceDataTabsTrigger value="update">{t("Upload & ingest")}</ReferenceDataTabsTrigger>
            <ReferenceDataTabsTrigger value="versions">{t("Versions & audit")}</ReferenceDataTabsTrigger>
          </ReferenceDataTabsList>

          <ReferenceDataTabsContent value="records" className="mt-5">
            <RecordsTab tableName={tableName} onStaged={() => setTab("versions")} />
          </ReferenceDataTabsContent>
          <ReferenceDataTabsContent value="update" className="mt-5">
            <UpdateTab
              tableName={tableName}
              file={draftFile}
              setFile={setDraftFile}
              runId={draftRunId}
              setRunId={setDraftRunId}
            />
          </ReferenceDataTabsContent>
          <ReferenceDataTabsContent value="versions" className="mt-5">
            <VersionsTab tableName={tableName} />
          </ReferenceDataTabsContent>
        </ReferenceDataTabs>
      </div>
    </div>
  );
}
